import React, { useCallback, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { SxProps, Theme } from '@mui/material/styles';
import { ReactComponent as IcBaselineDeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import { ReactComponent as IcRoundPlusIcon } from '../../static/images/svg/ic_round-plus.svg';
import { ReactComponent as TablerEditIcon } from '../../static/images/svg/tabler_edit.svg';

/** 设计稿「ic:round-plus」实心新增钮与行内「编辑 / 删除」幽灵图标的语义类型 */
export type CardToolbarIconButtonKind = 'add' | 'edit' | 'delete';

/** 根节点 class，供首屏等覆盖 `MUIDataTable` 工具栏对 IconButton 的全局 40px 样式 */
export const CARD_TOOLBAR_ICON_BTN_CLASS = 'card-toolbar-icon-btn';

/** 卡片区工具条图标按钮（云 RBP 首屏等）：32px 方钮，与设计节点 1841:52553、1843:52584 一致 */
export type CardToolbarIconButtonProps = {
    /** 按钮语义：新增为主题紫底白标；编辑/删除为线框风格图标 */
    kind: CardToolbarIconButtonKind;
    /** 点击回调，支持异步；内部可短时禁用防连点 */
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
    /** 是否禁用 */
    disabled?: boolean;
    /** Tooltip 与 aria-label 文案（须来自 i18n） */
    ariaLabel: string;
    /** 合并到 IconButton 的 `sx`（后写覆盖默认色，用于各业务与设计稿对齐） */
    sx?: SxProps<Theme>;
};

/** 设计稿中行内图标点击区边长（px） */
const TOOLBAR_ICON_BOX_PX = 32;
/** 实心「+」内图标尺寸（px），对应稿面约 18px 可视区 */
const ADD_INNER_ICON_PX = 18;
/** 行内编辑/删除 SVG 在 32px 容器内的近似边长（px） */
const ROW_GLYPH_PX = 22;

/**
 * 卡片区标题栏/表格操作列用图标按钮：对齐 Figma 1838:52035 内嵌「+」与行编辑删除。
 * @param props - 类型、点击、禁用与无障碍文案
 */
export function CardToolbarIconButton(props: CardToolbarIconButtonProps): JSX.Element {
    const { kind, onClick, disabled = false, ariaLabel, sx: sxProp } = props;
    /** 异步点击进行中，避免重复提交 */
    const [pending, setPending] = useState(false);

    const handleClick = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            if (!onClick || disabled || pending) {
                return;
            }
            setPending(true);
            try {
                await onClick(event);
            } finally {
                setPending(false);
            }
        },
        [disabled, onClick, pending]
    );

    const isAdd = kind === 'add';
    const isBusy = disabled || pending;

    return (
        <Tooltip title={ariaLabel} placement="top">
            <span>
                <IconButton
                    className={CARD_TOOLBAR_ICON_BTN_CLASS}
                    aria-label={ariaLabel}
                    disabled={isBusy}
                    disableRipple
                    onClick={handleClick}
                    sx={[
                        {
                            width: TOOLBAR_ICON_BOX_PX,
                            height: TOOLBAR_ICON_BOX_PX,
                            minWidth: TOOLBAR_ICON_BOX_PX,
                            minHeight: TOOLBAR_ICON_BOX_PX,
                            maxWidth: TOOLBAR_ICON_BOX_PX,
                            maxHeight: TOOLBAR_ICON_BOX_PX,
                            boxSizing: 'border-box',
                            flexShrink: 0,
                            p: 0,
                            borderRadius: '8px',
                            ...(isAdd
                                ? {
                                      backgroundColor: 'var(--icon-primary-300)',
                                      color: 'var(--fill-neutral-100)',
                                      '&:hover': {
                                          backgroundColor: 'var(--icon-primary-200)',
                                      },
                                      '&.Mui-disabled': {
                                          backgroundColor: 'var(--icon-primary-300)',
                                          color: 'var(--fill-neutral-100)',
                                          opacity: 0.5,
                                      },
                                  }
                                : {
                                      backgroundColor: 'transparent',
                                      color: kind === 'delete' ? 'var(--func-error)' : 'var(--icon-primary-300)',
                                      '&:hover': {
                                          backgroundColor: 'var(--fill-neutral-200)',
                                      },
                                  }),
                        },
                        ...(sxProp != null ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : []),
                    ]}
                >
                    {isAdd ? (
                        <IcRoundPlusIcon width={ADD_INNER_ICON_PX} height={ADD_INNER_ICON_PX} aria-hidden />
                    ) : kind === 'delete' ? (
                        <IcBaselineDeleteForeverIcon width={ROW_GLYPH_PX} height={ROW_GLYPH_PX} aria-hidden />
                    ) : (
                        <TablerEditIcon width={ROW_GLYPH_PX} height={ROW_GLYPH_PX} aria-hidden />
                    )}
                </IconButton>
            </span>
        </Tooltip>
    );
}
